import {
  LICENSE_STATUS_ACTIVE,
  LICENSE_STATUS_NOT_RENEWING,
  LICENSE_STATUS_TRIAL,
} from "@/v2-backoffice-client/license/license.constants"
import type { UsageMcpPortfolioQuery } from "@/v2-backoffice-client/usage/mcp/__types__/usage.mcpPortfolio.query"

export type PortfolioMcpCustomer =
  UsageMcpPortfolioQuery["getUsageMcpForPortfolio"]["Customers"][number]

export type PortfolioMcpMember =
  UsageMcpPortfolioQuery["getUsageMcpForPortfolio"]["Members"][number]

export type PortfolioMcpRefusedRead = {
  label: string
  tone: "positive" | "warning" | "neutral"
}

/**
 * Where a customer with refused sign-ins stands today, from its MCP license
 * posture and whether anyone has called a tool since the last refusal: back
 * in (licensed and calling again), licensed but not back yet, or still
 * outside. The one-off report had to dig this out of traces by hand.
 */
export function deriveMcpRefusedRead(
  customer: PortfolioMcpCustomer,
): PortfolioMcpRefusedRead {
  const isLicensed =
    customer.LicenseStatusCode === LICENSE_STATUS_TRIAL ||
    customer.LicenseStatusCode === LICENSE_STATUS_ACTIVE ||
    customer.LicenseStatusCode === LICENSE_STATUS_NOT_RENEWING
  const backIn =
    customer.LastActiveAt != null &&
    customer.LastDeniedAt != null &&
    new Date(customer.LastActiveAt) >= new Date(customer.LastDeniedAt)

  if (isLicensed && backIn) {
    return { label: "Licensed, back in use", tone: "positive" }
  }
  if (isLicensed) {
    return { label: "Licensed, not back yet", tone: "neutral" }
  }
  return { label: "Still outside", tone: "warning" }
}

/** Customers with refused sign-ins in the window, most attempts first. */
export function selectMcpRefusedCustomers(customers: PortfolioMcpCustomer[]) {
  return customers
    .filter((customer) => customer.Denied28d > 0)
    .toSorted(
      (a, b) =>
        b.Denied28d - a.Denied28d ||
        b.DeniedUsers28d - a.DeniedUsers28d ||
        a.Name.localeCompare(b.Name),
    )
}

export type PortfolioMcpMemberGroup = {
  externalCustomerId: string
  customerName: string
  members: PortfolioMcpMember[]
  calls28d: number
  callsTotal: number
}

/**
 * The appendix: every person grouped by customer, customers with the most
 * 28-day calls first and, within one, the busiest people first.
 */
export function groupMcpMembersByCustomer(
  members: PortfolioMcpMember[],
): PortfolioMcpMemberGroup[] {
  const groups = new Map<string, PortfolioMcpMemberGroup>()
  for (const member of members) {
    const group = groups.get(member.ExternalCustomerId) ?? {
      externalCustomerId: member.ExternalCustomerId,
      customerName: member.CustomerName,
      members: [],
      calls28d: 0,
      callsTotal: 0,
    }
    group.members.push(member)
    group.calls28d += member.Calls28d
    group.callsTotal += member.CallsTotal
    groups.set(member.ExternalCustomerId, group)
  }
  return [...groups.values()]
    .map((group) => ({
      ...group,
      members: group.members.toSorted(
        (a, b) => b.Calls28d - a.Calls28d || b.CallsTotal - a.CallsTotal,
      ),
    }))
    .toSorted(
      (a, b) =>
        b.calls28d - a.calls28d ||
        b.callsTotal - a.callsTotal ||
        a.customerName.localeCompare(b.customerName),
    )
}

/** "Anna Berg", or the e-mail, or "User 42" — never blank. */
export function formatMcpMemberName(member: {
  UserId: number
  FirstName?: string | null
  LastName?: string | null
  Email?: string | null
}) {
  return (
    `${member.FirstName ?? ""} ${member.LastName ?? ""}`.trim() ||
    member.Email ||
    `User ${member.UserId}`
  )
}

export type PortfolioMcpConcentration = {
  /** The customers that carry the volume, biggest first. */
  top: {
    externalCustomerId: string
    name: string
    calls: number
    share: number
  }[]
  /** Everyone else with calls in the window, as one slice. */
  rest: { count: number; calls: number; share: number }
  /** Share of the window's calls the top customers account for. */
  topShare: number
  calls28d: number
  /** Customers that have used it before but not in the last four weeks. */
  quiet: string[]
}

const CONCENTRATION_TOP = 4

/**
 * Who carries the last 28 days' calls: the top few customers as slices of
 * the whole, everyone else as one, and the customers that have gone quiet.
 * The picture behind "the volume is concentrated".
 */
export function deriveMcpConcentration(
  customers: PortfolioMcpCustomer[],
  calls28d: number,
): PortfolioMcpConcentration {
  const active = customers
    .filter((customer) => customer.Calls28d > 0)
    .toSorted((a, b) => b.Calls28d - a.Calls28d)
  const total = Math.max(calls28d, 1)
  const top = active.slice(0, CONCENTRATION_TOP).map((customer) => ({
    externalCustomerId: customer.ExternalCustomerId,
    name: customer.Name,
    calls: customer.Calls28d,
    share: customer.Calls28d / total,
  }))
  const restCustomers = active.slice(CONCENTRATION_TOP)
  const restCalls = restCustomers.reduce(
    (sum, customer) => sum + customer.Calls28d,
    0,
  )
  return {
    top,
    rest: {
      count: restCustomers.length,
      calls: restCalls,
      share: restCalls / total,
    },
    topShare: top.reduce((sum, customer) => sum + customer.share, 0),
    calls28d,
    quiet: customers
      .filter((customer) => customer.Calls28d === 0 && customer.CallsTotal > 0)
      .map((customer) => customer.Name),
  }
}
